import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, RefreshCw, Smartphone, Wifi, Clock, AlertTriangle, Send, Zap } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

export default function WebhookStatusPage() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastCheck, setLastCheck] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testText, setTestText] = useState('22K: 14770, 18K: 12195, Silver: 285');
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, historyRes] = await Promise.all([
        api.get('/kerala-bullion/webhook/status'),
        api.get('/kerala-bullion/webhook/history')
      ]);
      setStatus(statusRes.data);
      setHistory(historyRes.data.history || []);
      setLastCheck(new Date());
    } catch (error) {
      setStatus({ success: false, error: error.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handleTest = async () => {
    setTesting(true);
    try {
      const response = await api.post('/kerala-bullion/webhook/notification', {
        notification_text: testText,
        secret_key: 'marissa-gold-webhook-2024'
      });
      
      if (response.data.success) {
        toast.success('Test successful! Rates updated.');
        fetchStatus();
      } else {
        toast.error(response.data.message || 'Test failed');
      }
    } catch (error) {
      toast.error('Test failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTesting(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getTimeSince = (timestamp) => {
    if (!timestamp) return null;
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${Math.floor(diffHours / 24)} day(s) ago`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] to-[#1a3a5c] p-4 md:p-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#C9A967]/20 rounded-full mb-4">
            <Smartphone className="h-10 w-10 text-[#C9A967]" />
          </div>
          <h1 className="text-3xl font-bold text-white">Webhook Status</h1>
          <p className="text-gray-300 mt-2">Monitor rates received from your Android phone</p>
        </div>

        {/* Status Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Connection Status Header */}
          <div className={`p-6 ${status?.has_webhook_rates ? 'bg-green-500' : 'bg-gray-500'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {status?.has_webhook_rates ? (
                  <CheckCircle className="h-8 w-8 text-white" />
                ) : (
                  <XCircle className="h-8 w-8 text-white" />
                )}
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {status?.has_webhook_rates ? 'Receiving Rates' : 'Waiting for Rates'}
                  </h2>
                  <p className="text-white/80 text-sm">
                    {status?.has_webhook_rates 
                      ? 'Android automation is working!' 
                      : 'No rates received yet from phone'}
                  </p>
                </div>
              </div>
              <button
                onClick={fetchStatus}
                disabled={loading}
                className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
              >
                <RefreshCw className={`h-5 w-5 text-white ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Rates Display */}
          {status?.has_webhook_rates && status.rates && (
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">
                Current Rates from Phone
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-yellow-50 rounded-xl">
                  <p className="text-xs text-yellow-600 font-medium">22K GOLD</p>
                  <p className="text-2xl font-bold text-yellow-700">
                    ₹{status.rates.gold_22k?.toLocaleString() || '—'}
                  </p>
                  <p className="text-xs text-gray-500">per gram</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-xl">
                  <p className="text-xs text-yellow-600 font-medium">18K GOLD</p>
                  <p className="text-2xl font-bold text-yellow-700">
                    ₹{status.rates.gold_18k?.toLocaleString() || '—'}
                  </p>
                  <p className="text-xs text-gray-500">per gram</p>
                </div>
                <div className="text-center p-4 bg-gray-100 rounded-xl">
                  <p className="text-xs text-gray-600 font-medium">999 SILVER</p>
                  <p className="text-2xl font-bold text-gray-700">
                    ₹{status.rates.silver_999?.toLocaleString() || '—'}
                  </p>
                  <p className="text-xs text-gray-500">per gram</p>
                </div>
              </div>
            </div>
          )}

          {/* Timestamp Info */}
          <div className="p-6 bg-gray-50">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm">Last Rate Update:</span>
                </div>
                <span className="text-sm font-medium text-gray-800">
                  {status?.timestamp ? formatTime(status.timestamp) : 'Never'}
                </span>
              </div>
              {status?.timestamp && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Time ago:</span>
                  <span className="text-sm text-gray-600">{getTimeSince(status.timestamp)}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-600">
                  <Wifi className="h-4 w-4" />
                  <span className="text-sm">Status Page Checked:</span>
                </div>
                <span className="text-sm text-gray-600">
                  {lastCheck ? lastCheck.toLocaleTimeString() : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Setup Instructions */}
          {!status?.has_webhook_rates && (
            <div className="p-6 border-t border-gray-100">
              <div className="flex items-start gap-3 text-amber-700 bg-amber-50 p-4 rounded-xl">
                <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Setup Required</p>
                  <p className="text-sm mt-1">
                    Install MacroDroid on your Android phone and configure it to send 
                    Kerala Bullion notifications to this webhook.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Test Panel */}
          <div className="p-6 border-t border-gray-100">
            <button
              onClick={() => setShowTestPanel(!showTestPanel)}
              className="w-full flex items-center justify-between p-3 bg-blue-50 rounded-xl text-blue-700 hover:bg-blue-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                <span className="font-medium">Test Webhook</span>
              </div>
              <span className="text-sm">{showTestPanel ? '▲' : '▼'}</span>
            </button>
            
            {showTestPanel && (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sample Notification Text:
                  </label>
                  <textarea
                    value={testText}
                    onChange={(e) => setTestText(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter notification text like: 22K: 14770, 18K: 12195, Silver: 285"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter text similar to what Kerala Bullion notifications contain
                  </p>
                </div>
                
                <button
                  onClick={handleTest}
                  disabled={testing || !testText.trim()}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {testing ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Send Test
                    </>
                  )}
                </button>
                
                <div className="bg-gray-100 p-3 rounded-lg">
                  <p className="text-xs font-medium text-gray-700 mb-1">How to test from MacroDroid:</p>
                  <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
                    <li>Open MacroDroid app</li>
                    <li>Find your "Kerala Bullion Rate Push" macro</li>
                    <li>Tap the 3-dot menu → "Test Actions"</li>
                    <li>It will send a test request to the webhook</li>
                  </ol>
                </div>
              </div>
            )}
          </div>

          {/* History Panel */}
          <div className="p-6 border-t border-gray-100">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="w-full flex items-center justify-between p-3 bg-purple-50 rounded-xl text-purple-700 hover:bg-purple-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span className="font-medium">Webhook History</span>
                {history.length > 0 && (
                  <span className="bg-purple-200 text-purple-800 text-xs px-2 py-0.5 rounded-full">
                    {history.length}
                  </span>
                )}
              </div>
              <span className="text-sm">{showHistory ? '▲' : '▼'}</span>
            </button>
            
            {showHistory && (
              <div className="mt-4 space-y-2 max-h-80 overflow-y-auto">
                {history.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">
                    No webhook calls yet. Test the webhook or wait for a notification.
                  </p>
                ) : (
                  history.map((entry, index) => (
                    <div 
                      key={`wh-${entry.timestamp}-${index}`}
                      className={`p-3 rounded-lg border ${
                        entry.success 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          {entry.success ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          <span className={`text-sm font-medium ${
                            entry.success ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {entry.success ? 'Success' : 'Failed'}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {formatTime(entry.timestamp)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 truncate">
                        Text: {entry.received_text || '(empty)'}
                      </p>
                      {entry.success && entry.rates && (
                        <p className="text-xs text-green-600 mt-1">
                          22K: ₹{entry.rates.gold_22k} | 18K: ₹{entry.rates.gold_18k} | Silver: ₹{entry.rates.silver_999}
                        </p>
                      )}
                      {!entry.success && entry.error && (
                        <p className="text-xs text-red-600 mt-1">
                          Error: {entry.error}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* API Info */}
        <div className="mt-6 bg-white/10 backdrop-blur rounded-xl p-4">
          <h3 className="text-white font-medium mb-2">Webhook Endpoint:</h3>
          <code className="text-xs text-[#C9A967] bg-black/30 px-3 py-2 rounded block overflow-x-auto">
            POST /api/kerala-bullion/webhook/notification
          </code>
          <p className="text-gray-400 text-xs mt-2">
            Auto-refreshes every 30 seconds • Last check: {lastCheck?.toLocaleTimeString() || '—'}
          </p>
        </div>
      </div>
    </div>
  );
}
